import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Badge, Button, Card, ErrorBox, Field, Input } from "../components/ui";
import { api } from "../lib/api";
import {
  decimalToInput,
  formatDate,
  formatDateAr,
  formatDecimal,
  parseUserAmount,
} from "../lib/format";

interface MonthlyFee {
  id: string;
  amount: string;
  chargeDay: number;
  dueDays: number | null;
  startMonth: string;
  activatedAt: string;
  pausedAt: string | null;
}

const thisMonth = () =>
  `${new Intl.DateTimeFormat("en-CA", { timeZone: "America/Argentina/Buenos_Aires" })
    .format(new Date())
    .slice(0, 7)}-01`;

/** The company's monthly fee: what the system charges by itself every month (spec 015 FR-021). */
export function MonthlyFeePanel({ companyId }: { companyId: string }) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const fee = useQuery({
    queryKey: ["receivables", "monthly-fee", companyId],
    queryFn: async () =>
      (
        await api.get<{ fee: MonthlyFee | null }>(
          `/api/receivables/companies/${companyId}/monthly-fee`,
        )
      ).fee,
  });
  const refresh = () => client.invalidateQueries({ queryKey: ["receivables"] });
  const toggle = useMutation({
    mutationFn: (action: "pause" | "resume") =>
      api.post(`/api/receivables/companies/${companyId}/monthly-fee/${action}`),
    onSuccess: refresh,
  });
  const current = fee.data;

  return (
    <Card
      className="mb-4"
      title={
        <span className="flex items-center gap-2">
          Abono mensual
          {current ? (
            current.pausedAt ? (
              <Badge tone="warning">Pausado</Badge>
            ) : (
              <Badge tone="success">Activo</Badge>
            )
          ) : null}
        </span>
      }
      actions={
        current && !editing ? (
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Cambiar
            </Button>
            <Button
              variant="secondary"
              disabled={toggle.isPending}
              onClick={() => toggle.mutate(current.pausedAt ? "resume" : "pause")}
            >
              {current.pausedAt ? "Reanudar" : "Pausar"}
            </Button>
          </>
        ) : null
      }
    >
      <ErrorBox error={fee.error ?? toggle.error} />
      {editing || (fee.isSuccess && !current) ? (
        <FeeForm
          companyId={companyId}
          current={current ?? null}
          onDone={async () => {
            setEditing(false);
            await refresh();
          }}
          onCancel={current ? () => setEditing(false) : undefined}
        />
      ) : current ? (
        <p className="text-sm">
          $ {formatDecimal(current.amount)} por mes, el día {current.chargeDay}
          {current.chargeDay > 28 ? " (o el último del mes)" : ""}
          {current.dueDays !== null ? `, vence a los ${current.dueDays} días` : ", sin vencimiento"}
          . Desde {formatDate(current.startMonth).slice(3)}.{" "}
          {current.pausedAt ? (
            <span className="text-amber-800">
              Pausado el {formatDateAr(current.pausedAt)}: no se generan cargos.
            </span>
          ) : (
            <span className="text-brand-muted">
              El sistema carga el cargo solo cada mes; si lo anulás, ese mes no se vuelve a generar.
            </span>
          )}
        </p>
      ) : null}
    </Card>
  );
}

function FeeForm({
  companyId,
  current,
  onDone,
  onCancel,
}: {
  companyId: string;
  current: MonthlyFee | null;
  onDone: () => Promise<void>;
  onCancel: (() => void) | undefined;
}) {
  const [amount, setAmount] = useState(decimalToInput(current?.amount));
  const [chargeDay, setChargeDay] = useState(String(current?.chargeDay ?? 1));
  const [dueDays, setDueDays] = useState(
    current?.dueDays === null ? "" : String(current?.dueDays ?? 10),
  );
  const [startMonth, setStartMonth] = useState((current?.startMonth ?? thisMonth()).slice(0, 7));
  const [error, setError] = useState<string>();
  const save = useMutation({
    mutationFn: (body: object) =>
      api.put(`/api/receivables/companies/${companyId}/monthly-fee`, body),
    onSuccess: onDone,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseUserAmount(amount);
    if (!parsed || Number(parsed) <= 0) {
      setError("Escribí un importe mayor que cero.");
      return;
    }
    setError(undefined);
    save.mutate({
      amount: parsed,
      chargeDay: Number(chargeDay),
      dueDays: dueDays === "" ? null : Number(dueDays),
      startMonth: `${startMonth}-01`,
    });
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-5">
      <Field label="Importe mensual" error={error}>
        <Input
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="decimal"
          required
        />
      </Field>
      <Field label="Día de cobro" hint="Del 1 al 31; en meses más cortos, el último día.">
        <Input
          type="number"
          min={1}
          max={31}
          value={chargeDay}
          onChange={(e) => setChargeDay(e.target.value)}
          required
        />
      </Field>
      <Field label="Días para vencer" hint="Vacío: sin vencimiento.">
        <Input
          type="number"
          min={0}
          max={90}
          value={dueDays}
          onChange={(e) => setDueDays(e.target.value)}
        />
      </Field>
      <Field label="Desde el mes">
        <Input
          type="month"
          value={startMonth}
          onChange={(e) => setStartMonth(e.target.value)}
          required
        />
      </Field>
      <div className="flex items-end gap-2">
        <Button type="submit" disabled={save.isPending}>
          {current ? "Guardar cambios" : "Activar abono"}
        </Button>
        {onCancel ? (
          <Button variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-brand-muted md:col-span-5">
        {current
          ? "Un cambio vale desde el próximo cargo; los cargos ya generados no cambian."
          : "Los meses anteriores a hoy no se cargan solos: si hacen falta, cargalos a mano."}
      </p>
      <div className="md:col-span-5">
        <ErrorBox error={save.error} />
      </div>
    </form>
  );
}
